import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, assetUrl, thumbUrl } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import PageHero from "@/components/PageHero";
import { useSeo } from "@/lib/seo";
import { LocationLinks } from "@/components/PlaceCards";
import {
  Broadcast, PlayCircle, CalendarBlank, Warning, MapPin, ArrowLeft, Code,
} from "@phosphor-icons/react";

const TYPE_LABEL = { disaster: "Disaster", news: "News event", conservation: "Conservation", other: "Event" };

function fmtDate(d) {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
}

export default function EventPage() {
  const { id } = useParams();
  const [data, setData] = useState(undefined);

  useEffect(() => {
    setData(undefined);
    axios.get(`${API}/public/event-sets/${encodeURIComponent(id)}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [id]);

  useSeo({
    title: data?.title ? `${data.title} — PhotoPulse Event` : "Event — PhotoPulse",
    description: data?.description || `Photos from ${data?.title || "this event"} on PhotoPulse.pro`,
  });

  if (data === undefined)
    return (
      <PublicLayout>
        <div className="mx-auto max-w-[1100px] px-5 py-8" data-testid="event-loading">
          <div className="h-56 animate-pulse rounded-3xl bg-zinc-100" />
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-xl bg-zinc-100" />)}
          </div>
        </div>
      </PublicLayout>
    );
  if (data === null)
    return <PublicLayout><div className="mx-auto max-w-2xl px-5 py-20 text-center" data-testid="event-notfound"><p className="text-muted-foreground">This event isn't available. <Link to="/photos" className="font-semibold text-[#F27A54]">Explore places</Link>.</p></div></PublicLayout>;

  const dateRange = [fmtDate(data.start_date), fmtDate(data.end_date)].filter(Boolean).join(" – ");

  return (
    <PublicLayout>
      <div className="mx-auto max-w-[1100px] px-5 py-8">
        <Link to="/photos" data-testid="event-back" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-600 hover:text-[#F27A54]">
          <ArrowLeft size={16} weight="bold" /> Explore
        </Link>

        <PageHero
          eyebrow={TYPE_LABEL[data.type] || "Event"}
          icon={data.type === "disaster" ? Warning : Broadcast}
          title={data.title}
          image={data.cover_image ? assetUrl(data.cover_image) : undefined}
          subtitle={`${data.photo_count} photo${data.photo_count === 1 ? "" : "s"} in this set`}
          seed={data.evt_id}
        />

        <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-zinc-600" data-testid="event-meta">
          <span className="rounded-full border border-border bg-white px-3 py-1 font-mono text-xs">{data.evt_id}</span>
          {data.severity && <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">Severity: {data.severity}</span>}
          {dateRange && <span className="flex items-center gap-1.5"><CalendarBlank size={15} weight="bold" /> {dateRange}</span>}
          {(data.location?.city || data.location?.country) && (
            <span className="flex items-center gap-1.5"><MapPin size={15} weight="fill" className="text-[#F27A54]" /><LocationLinks location={data.location} /></span>
          )}
        </div>

        {data.description && <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-700 sm:text-base" data-testid="event-description">{data.description}</p>}

        {/* Photos */}
        <div className="mt-8">
          <h2 className="font-display text-lg font-black tracking-tight">Photos</h2>
          {(!data.photos || data.photos.length === 0) ? (
            <p className="mt-4 rounded-xl border border-dashed border-border py-10 text-center text-sm text-muted-foreground" data-testid="event-empty">No public photos in this event yet.</p>
          ) : (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="event-photos">
              {data.photos.map((m) => (
                <Link key={m.id} to={m.photo_id ? `/photo/${m.photo_id}` : `/p/${m.id}`} data-testid={`event-photo-${m.id}`}
                  className="group overflow-hidden rounded-xl border border-border bg-card transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
                  <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
                    <img src={thumbUrl(m.image_url)} alt={m.alt_text || m.title || "photo"} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    {m.media_type === "video" && <PlayCircle size={36} weight="fill" className="absolute inset-0 m-auto text-white/90 drop-shadow" />}
                  </div>
                  <div className="p-3">
                    <p className="line-clamp-1 text-sm font-bold">{m.title || "Untitled"}</p>
                    {m.location?.city && <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground"><MapPin size={11} weight="fill" /> {m.location.city}</p>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Pull API hint for sister sites */}
        <div className="mt-10 rounded-2xl border border-border bg-[#FBFAF8] p-5" data-testid="event-pull-api">
          <p className="flex items-center gap-2 overline text-[10px] text-[#2D6A4F]"><Code size={14} weight="fill" /> For publishers</p>
          <p className="mt-1.5 text-sm text-zinc-700">Pull this whole event set (metadata + attributed photos) in one call:</p>
          <code className="mt-2 block overflow-x-auto rounded-lg bg-white px-3 py-2 text-[12px] text-zinc-700 ring-1 ring-border">GET /api/public/event-sets/{data.evt_id}</code>
        </div>
      </div>
    </PublicLayout>
  );
}
